import React, { useCallback, useState } from "react";

import { useForm } from "react-hook-form-7";
import { Link as RRLink } from "react-router-dom";

import {
  Alert,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Stack,
  StackDivider,
} from "@chakra-ui/react";

import {
  GetViewerDocument,
  useSetCredential,
  useUserLogin,
} from "../../../graphql/generated/definitions";

import useTranslations from "../../../hooks/useTranslations";

import LoginLayout from "../layout/LoginLayout";
import NavigationTabs from "../components/NavigationTabs";
import PasswordInput from "../components/PasswordInput";

type LoginFormInputs = { email: string; password: string };

export default function LoginPage() {
  const t = useTranslations();

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<LoginFormInputs>({ mode: "onChange" });

  const [userLogin, { error: userLoginError }] = useUserLogin();
  const [setCredential] = useSetCredential();

  const onSubmit = useCallback(async ({ email, password }: LoginFormInputs) => {
    try {
      const credentialData = await userLogin({
        variables: {
          email,
          password,
        },
      });

      setCredential({
        variables: credentialData.data.userLogin.credentials,
        refetchQueries: [{ query: GetViewerDocument }],
      });
    } catch {
      // Should not append
    }
  }, []);

  return (
    <LoginLayout title={t("login.gladToSeeYou") as string}>
      <Stack divider={<StackDivider color="gray.200" />} spacing="6">
        <NavigationTabs />
        <form onSubmit={handleSubmit(onSubmit)} id="login-form">
          <Stack spacing="5">
            <FormControl
              id="email"
              isRequired
              isInvalid={!!errors?.email?.message}
            >
              <FormLabel>{t("forms.fields.email")}</FormLabel>
              <Input
                size="lg"
                type="email"
                // This looks rather ugly but native form validation block the one made by RHF
                isRequired={false}
                placeholder="marc.aurele@gmail.com"
                aria-required="true"
                {...register("email", {
                  required: t("forms.errors.required") as string,
                })}
              />
              <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
            </FormControl>
            <Stack>
              <FormControl
                id="password"
                isRequired
                isInvalid={!!errors?.password?.message}
              >
                <FormLabel>{t("forms.fields.password")}</FormLabel>
                <PasswordInput
                  size="lg"
                  // This looks rather ugly but native form validation block the one made by RHF
                  isRequired={false}
                  placeholder={t("login.passwordPlaceholder") as string}
                  aria-required="true"
                  {...register("password", {
                    required: t("forms.errors.required") as string,
                  })}
                />
                <FormErrorMessage>{errors?.password?.message}</FormErrorMessage>
              </FormControl>
              <Flex direction="row" justifyContent="flex-end">
                <Link
                  id="password-lost-link"
                  as={RRLink}
                  to="/forgotten-password"
                  color="brand"
                  fontSize="sm"
                  fontWeight="semibold"
                >
                  {t("header.passwordLost")}
                </Link>
              </Flex>
            </Stack>
            {!!userLoginError && (
              <Alert status="warning" id="login-form-error">
                {t("forms.errors.emailOrPassword")}
              </Alert>
            )}
            <Stack spacing="6" pt="4">
              <Button
                disabled={!isValid}
                type="submit"
                isFullWidth
                colorScheme="primary"
              >
                {t("forms.actions.connection")}
              </Button>
              <Link
                as={RRLink}
                id="saml-sso"
                to="/saml-sso"
                color="secondary"
                textAlign="center"
                fontSize="sm"
                fontWeight="semibold"
              >
                {t("saml.connectWithSSO")}
              </Link>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </LoginLayout>
  );
}
